import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLogger } from "shared/@hooks/useLogger";
import { useQueryParams } from "shared/@hooks/useQueryParams";
import Link from "shared/Link";
import Loader from "shared/Loader";
import PinLabel from "shared/PinLabel";
import Table from "shared/Table";
import { TableSortValue } from "shared/TableSortHeader";
import NameBubble from "../NameBubble";
import styles from "./index.module.css";
import { useAuthedRequest } from "shared/@auth/useAuthedRequest";
import { RequestCache } from "shared/@hooks/useRequestCache";
import TableFilterSortHeader from "../TableFilterSortHeader";
import {
  applyTableFilters,
  defaultStatusFilter,
  membershipOf,
  parseCsvFilter,
  pinOf,
  sortRows,
  sortToHeaderValue,
  statusOf,
  toCsvFilter,
  toOptions,
} from "../tableUtils";

export default function Upline({ iboNumber }) {
  const { logError } = useLogger({ prefix: "LOS/Upline" });
  const query = useQueryParams();
  const q = query.get();

  // Clear all upline filter/sort params when iboNumber changes or component unmounts,
  // so filters do not carry over when navigating to a different IBO.
  useEffect(() => {
    return () => {
      query.set({
        "los-upline-sort": null,
        "los-upline-status": null,
        "los-upline-membership": null,
        "los-upline-pin": null,
      });
    };
  }, [iboNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const sort = q["los-upline-sort"] ?? null;
  const statusFilters = parseCsvFilter(
    q["los-upline-status"],
    defaultStatusFilter
  );
  const membershipFilters = parseCsvFilter(q["los-upline-membership"]);
  const pinFilters = parseCsvFilter(q["los-upline-pin"]);

  const hasExplicitPinFilter = Object.prototype.hasOwnProperty.call(
    q,
    "los-upline-pin"
  );
  const hasExplicitMembershipFilter = Object.prototype.hasOwnProperty.call(
    q,
    "los-upline-membership"
  );
  // Status defaults to current and is treated as an explicit filter by design.
  const hasExplicitStatusFilter = true;

  const isModified =
    Object.prototype.hasOwnProperty.call(q, "los-upline-sort") ||
    Object.prototype.hasOwnProperty.call(q, "los-upline-status") ||
    Object.prototype.hasOwnProperty.call(q, "los-upline-membership") ||
    Object.prototype.hasOwnProperty.call(q, "los-upline-pin");

  const resetFilters = () =>
    query.set({
      "los-upline-sort": null,
      "los-upline-status": null,
      "los-upline-membership": null,
      "los-upline-pin": null,
    });

  const cache = RequestCache.useContainer();
  const { data, loading, error } = useAuthedRequest(
    `/v1/membership/los/upline/${iboNumber}`,
    null,
    cache
  );

  if (error) {
    logError(error);
  }

  const [upline, setUpline] = useState(null);
  const [dropdownHeights, setDropdownHeights] = useState({});

  // Ensure upline is never reset to null once loaded.
  useEffect(() => {
    if (data != null) {
      setUpline(data.map((x) => x).reverse());
    }
  }, [data]);

  const statusOptions = useMemo(() => {
    return toOptions(
      (upline ?? []).map((item) => statusOf(item)),
      ["current"]
    );
  }, [upline]);

  const membershipOptions = useMemo(() => {
    return toOptions((upline ?? []).map((item) => membershipOf(item)));
  }, [upline]);

  const pinOptions = useMemo(() => {
    return toOptions((upline ?? []).map((item) => pinOf(item)));
  }, [upline]);

  const visibleRows = useMemo(() => {
    const filtered = applyTableFilters(upline ?? [], {
      statuses: statusFilters,
      memberships: membershipFilters,
      pins: pinFilters,
    });

    return sortRows(filtered, sort);
  }, [upline, sort, statusFilters, membershipFilters, pinFilters]);

  const implicitPinCheckedValues = useMemo(() => {
    return [...new Set(visibleRows.map((item) => pinOf(item)))];
  }, [visibleRows]);

  const implicitStatusCheckedValues = useMemo(() => {
    return [...new Set(visibleRows.map((item) => statusOf(item)))];
  }, [visibleRows]);

  const implicitMembershipCheckedValues = useMemo(() => {
    return [...new Set(visibleRows.map((item) => membershipOf(item)))];
  }, [visibleRows]);

  const onDropdownMenuStateChange = useCallback((key, isOpen, height) => {
    setDropdownHeights((prev) => {
      if (!isOpen || !height) {
        if (prev[key] == null) {
          return prev;
        }

        const next = { ...prev };
        delete next[key];
        return next;
      }

      if (prev[key] === height) {
        return prev;
      }

      return {
        ...prev,
        [key]: height,
      };
    });
  }, []);

  const dropdownBottomSpace = useMemo(() => {
    const heights = Object.values(dropdownHeights);
    const maxHeight = heights.length > 0 ? Math.max(...heights) : 0;
    return maxHeight > 0 ? maxHeight + 12 : 0;
  }, [dropdownHeights]);

  // Show loader while data is in transit.
  if (loading || upline == null) {
    return <Loader />;
  }

  return (
    <div
      className={styles.componentContainer}
      style={
        dropdownBottomSpace > 0
          ? { paddingBottom: `${dropdownBottomSpace}px` }
          : undefined
      }
    >
      <Table allowContentOverflow={true} className={styles.table}>
        <thead>
          <tr>
            <th>
              <div className={styles.nameHeader}>
                Name
                {isModified && (
                  <button className={styles.resetButton} onClick={resetFilters}>
                    Reset LOS
                  </button>
                )}
              </div>
            </th>
            <th>IBO Number</th>
            <th>
              <TableFilterSortHeader
                title="Pin Level"
                options={pinOptions}
                selectedValues={pinFilters}
                implicitCheckedValues={implicitPinCheckedValues}
                isExplicitSelection={hasExplicitPinFilter}
                onMenuStateChange={(isOpen, height) =>
                  onDropdownMenuStateChange("pin", isOpen, height)
                }
                onFilterChange={(next) => {
                  query.set({ "los-upline-pin": toCsvFilter(next) });
                }}
                sortValue={sortToHeaderValue(sort, "pin")}
                onSortChange={(value) => {
                  if (value === TableSortValue.ascending) {
                    query.set({ "los-upline-sort": "pin-asc" });
                  } else {
                    query.set({ "los-upline-sort": "pin-desc" });
                  }
                }}
              />
            </th>
            <th>
              <TableFilterSortHeader
                title="Status"
                options={statusOptions}
                selectedValues={statusFilters}
                implicitCheckedValues={implicitStatusCheckedValues}
                isExplicitSelection={hasExplicitStatusFilter}
                onMenuStateChange={(isOpen, height) =>
                  onDropdownMenuStateChange("status", isOpen, height)
                }
                onFilterChange={(next) => {
                  query.set({ "los-upline-status": toCsvFilter(next) });
                }}
                sortValue={sortToHeaderValue(sort, "status")}
                onSortChange={(value) => {
                  if (value === TableSortValue.ascending) {
                    query.set({ "los-upline-sort": "status-asc" });
                  } else {
                    query.set({ "los-upline-sort": "status-desc" });
                  }
                }}
              />
            </th>
            <th>
              <TableFilterSortHeader
                title="Membership"
                options={membershipOptions}
                selectedValues={membershipFilters}
                implicitCheckedValues={implicitMembershipCheckedValues}
                isExplicitSelection={hasExplicitMembershipFilter}
                onMenuStateChange={(isOpen, height) =>
                  onDropdownMenuStateChange("membership", isOpen, height)
                }
                onFilterChange={(next) => {
                  query.set({ "los-upline-membership": toCsvFilter(next) });
                }}
                sortValue={sortToHeaderValue(sort, "membership")}
                onSortChange={(value) => {
                  if (value === TableSortValue.ascending) {
                    query.set({ "los-upline-sort": "membership-asc" });
                  } else {
                    query.set({ "los-upline-sort": "membership-desc" });
                  }
                }}
              />
            </th>
          </tr>
        </thead>
        <tbody className={styles.tableBody}>
          {visibleRows.length > 0 ? (
            visibleRows.map((ibo, index) => (
              <tr className={styles.tableRow} key={ibo.iboNumber}>
                <td>
                  <div className={styles.bubbleWrap}>
                    {index > 0 && (
                      <div className={styles.uplineArrow}>
                        <div className={styles.uplineArrowHead}></div>
                        <div className={styles.uplineArrowLine}></div>
                      </div>
                    )}

                    <NameBubble {...ibo} />
                  </div>
                </td>
                <td>
                  <Link to={`/membership/lookup/ibo/${ibo.iboNumber}`}>
                    {ibo.iboNumber}
                  </Link>
                </td>
                <td>
                  <PinLabel pinLevel={ibo.pinLevel} />
                </td>
                <td className={styles.capitalize}>
                  {ibo.status?.toLowerCase() ?? (
                    <span className={styles.faded}>None</span>
                  )}
                </td>

                <td className={styles.capitalize}>
                  {ibo.membership?.toLowerCase() ?? (
                    <span className={styles.faded}>No Membership</span>
                  )}
                  {ibo.hasDigitalDelivery && ibo.hasDreamStream
                    ? " +"
                    : ibo.hasDigitalDelivery
                    ? " + DD"
                    : ibo.hasDreamStream
                    ? " + Topics"
                    : ""}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={5}>No results found.</td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
}
